import { truncateTextWithEllipsis } from '@utilities';

/**
 * gets option label based on prompt description
 * @param {Object} params - The parameters object.
 * @param {number} params.childLength - The maximum length for truncating the child text (optional).
 * @param {number} params.length - The maximum length for truncating the parent text (optional).
 * @param {Object} params.option - The option object to generate the label for.
 * @return {String|Node}
 */
export const getOptionLabel = ({ childLength, length, option }) => {
  if (!option) return '';

  const [rawParent, rawChild] = option?.prompt_desc?.split(' || ') || [];

  const parent = rawParent?.trim();
  const childRaw = rawChild?.replace(`(${parent})`, '').trim();

  const parentLabel = length ? truncateTextWithEllipsis({ length, text: parent }) : parent;

  const childLabel = childLength
    ? truncateTextWithEllipsis({ length: childLength, text: childRaw })
    : childRaw;

  if (childLabel) {
    return (
      <>
        {childLabel}
        <span>&nbsp;- {parentLabel}</span>
      </>
    );
  }

  if (option?.operatorInfo) return option?.metric?.name;

  return option?.name || '';
};

/**
 * Builds hierarchy constraints for every prompt paired with the active prompt.
 * @param {Object} params - The parameters object.
 * @param {String} params.promptKey - The active prompt key.
 * @param {String[][]} params.searchPrompts - Ordered hierarchy prompt pairings.
 * @param {Object.<String, Object[]>} params.selectedAnswers - Answers keyed by prompt key that determine whether constraints are applied.
 * @return {Object[]} Constraints containing related attribute IDs and prompt positions.
 */
export const getRelatedPromptConstraints = ({
  promptKey,
  searchPrompts = [],
  selectedAnswers = {},
}) => {
  return searchPrompts.flatMap((hierarchyPairing) => {
    if (!Array.isArray(hierarchyPairing)) return [];

    const currentPromptIndex = hierarchyPairing.indexOf(promptKey);
    if (currentPromptIndex === -1) {
      return [];
    }

    return hierarchyPairing
      .map((relatedPromptKey, relatedPromptIndex) => {
        if (relatedPromptKey === promptKey) return null;

        const attributeIds = [
          ...new Set(
            (selectedAnswers?.[relatedPromptKey] || [])
              .map(({ attributeId }) => attributeId)
              .filter(Boolean)
          ),
        ];

        return {
          attributeIds,
          currentPromptIndex,
          relatedPromptIndex,
          relatedPromptKey: relatedPromptKey,
        };
      })
      .filter(Boolean);
  });
};

/**
 * Finds an attribute's position in an ordered hierarchy.
 * @param {Object[]} attributes - Hierarchy attributes ordered from highest to lowest tier.
 * @param {String} attributeId - The attribute ID to find.
 * @return {Number} The attribute index, or -1 when it is not present.
 */
const getAttributeIndex = (attributes, attributeId) =>
  attributes.findIndex(({ id }) => id === attributeId);

/**
 * Finds the restrictive hierarchy boundary represented by the related prompt's answers.
 * @param {Object} params - The parameters object.
 * @param {String[]} params.attributeIds - Selected attribute IDs from the related prompt.
 * @param {Object[]} params.attributes - Non-custom attributes for the given prompt.
 * @param {Number} params.currentPromptIndex - Active prompt position in the prompt pairing.
 * @param {Number} params.relatedPromptIndex - Related prompt position in the prompt pairing.
 * @return {String|null} The boundary attribute ID, or null when no selected ID is in the hierarchy.
 */
export const getHierarchyBoundary = ({
  attributeIds = [],
  attributes = [],
  currentPromptIndex,
  relatedPromptIndex,
}) => {
  const selectedIndexes = attributeIds
    .map((attributeId) => getAttributeIndex(attributes, attributeId))
    .filter((index) => index >= 0);

  if (selectedIndexes.length === 0) {
    return null;
  }

  const isCurrentPromptBeforeRelated = currentPromptIndex < relatedPromptIndex;

  const hierarchyBoundaryIndex = isCurrentPromptBeforeRelated
    ? Math.max(...selectedIndexes)
    : Math.min(...selectedIndexes);

  return attributes[hierarchyBoundaryIndex]?.id || null;
};

/**
 * Filters prompt-supported attributes to the tiers permitted by all related prompt constraints.
 * @param {Object} params - The parameters object.
 * @param {Object[]} params.attributes - Attributes supported by the active prompt.
 * @param {Object[]} params.hierarchyAttributesOrdered - Canonical hierarchy ordered highest to lowest.
 * @param {Object[]} params.relatedPromptConstraints - Related answers and prompt positions.
 * @return {Object[]} The active prompt attributes permitted by hierarchy constraints.
 */
export const getHierarchyAttributes = ({
  attributes = [],
  hierarchyAttributesOrdered = [],
  relatedPromptConstraints = [],
}) => {
  const constraints = relatedPromptConstraints
    .map((constraint) => {
      const boundaryAttributeId = getHierarchyBoundary({
        attributeIds: constraint.attributeIds,
        attributes: hierarchyAttributesOrdered,
        currentPromptIndex: constraint.currentPromptIndex,
        relatedPromptIndex: constraint.relatedPromptIndex,
      });

      return boundaryAttributeId ? { ...constraint, boundaryAttributeId } : null;
    })
    .filter(Boolean);

  if (constraints.length === 0) {
    return attributes;
  }

  return attributes.filter((attribute) => {
    const candidateIndex = getAttributeIndex(hierarchyAttributesOrdered, attribute.id);
    if (candidateIndex < 0) return false;
    return constraints.every(({ boundaryAttributeId, currentPromptIndex, relatedPromptIndex }) => {
      const boundaryIndex = getAttributeIndex(hierarchyAttributesOrdered, boundaryAttributeId);
      if (boundaryIndex < 0) return false;
      const isCurrentPromptBeforeRelated = currentPromptIndex < relatedPromptIndex;

      return isCurrentPromptBeforeRelated
        ? candidateIndex >= boundaryIndex
        : candidateIndex <= boundaryIndex;
    });
  });
};
